import { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Clock, Upload, ExternalLink, Mail, Copy, Check } from 'lucide-react';
import { parseAMIFile } from '../lib/ami';
import { PEAK_CEILING_KW } from '../lib/ami/parser';
import { mergeAMIDatasets, shouldRefuseMerge } from '../lib/amiMerge';
import { summarizeAmiSeries } from '../lib/amiSummary';
import { regroupEstimatedRuns, type TimeRange } from '../lib/ami/suspectPeaks';
import { stepsFor, describeHistory, safePortalUrl, isUploadRoute } from '../lib/utilityRoutes';
import { buildCustomerRequestEmail, mailtoHref } from '../lib/customerRequest';
import {
  fillRequestForm,
  isUsableTemplate,
  localDate,
  requestFormValues,
  REQUEST_FORM_BUSINESS_DAYS,
} from '../lib/requestForm';

/**
 * The homeowner's page. Two routes share one link:
 *
 *   email_request: the authorization it always did. The homeowner reads what
 *   is being asked and clicks I Authorize (or declines); TrueLoad then emails
 *   the utility.
 *
 *   self_download: no signature step. The page opens straight on the utility's
 *   download steps and the upload area, and sending the file IS the
 *   authorization: the consent is recorded on the request at the moment the
 *   readings land, with the homeowner's IP, exactly as I Authorize records it
 *   on the other route. A separate "I Authorize" gate before a file the
 *   homeowner fetches and hands over themselves was a click that meant nothing.
 *   The file is read here in the browser by the same parser the contractor
 *   uses; only the readings and the summary go to the server, and the
 *   contractor never handles the file.
 *
 *   customer_request: the utility wants the request from its own customer
 *   (Burlington Electric Department). Step 1 writes that request for the
 *   homeowner, addressed to the utility, and opens it in their own email;
 *   step 2 is the self-download upload, for the file the utility sends back.
 *   TrueLoad never writes to the utility on this route.
 *
 *   customer_request with a request form (request_form_template on the row,
 *   snapshotted at send): the utility takes requests through its own website
 *   form (BED, 2026-10-23). The page opens that form pre-filled with what the
 *   request holds; the homeowner signs the utility's waiver and submits it
 *   there, then presses "I submitted the form", which ends their part. The
 *   utility sends the file to the contractor, so there is no upload step. A
 *   row with no template is a link sent before this and keeps the email step.
 */

interface ConsentRequest {
  id: string;
  status: 'pending' | 'consented' | 'declined';
  customer_name: string;
  customer_address: string;
  customer_phone: string;
  customer_email?: string | null;
  account_number: string | null;
  contractor_name: string;
  company_name: string;
  contractor_email: string;
  utility_name: string;
  utility_id: string | null;
  /** On customer_request, where the homeowner sends the request. */
  utility_email?: string | null;
  token_expires_at: string;
  created_at: string;
  consented_at: string | null;
  data_route?: 'email_request' | 'self_download' | 'customer_request' | null;
  /** Form route: the utility's form URL with {placeholders}. */
  request_form_template?: string | null;
  customer_submitted_at?: string | null;
}

interface InstructionCard {
  name: string;
  ami_status: string;
  portal_url: string | null;
  download_steps: string | null;
  export_format: string | null;
  history_months: number | null;
  rollout_note: string | null;
  instructions_verified: boolean;
}

interface UploadState {
  route: 'email_request' | 'self_download' | 'customer_request';
  open: boolean;
  expiresAt: string | null;
  uploadedAt: string | null;
  readingCount: number | null;
  firstReading: string | null;
  lastReading: string | null;
  feedback: string | null;
}

interface ConsentPageProps {
  token: string;
}

type PageStatus =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'already_responded'
  | 'error'
  | 'upload'
  | 'uploaded'
  | 'upload_closed'
  | 'form'
  | 'form_done';

interface ReadFile {
  points: Array<{ timestamp: string; kw: number }>;
  qualityNotes: string[];
  flagMessages: string[];
  /** Runs the files mark as utility-estimated, regrouped over the merged readings. */
  estimatedRuns: TimeRange[];
  generation: boolean;
  fileName: string;
  count: number;
  first: string | null;
  last: string | null;
  intervalMinutes: number | null;
  measurementPeriod: string;
}

const ACCEPT = '.csv,.txt,.tsv,.xlsx,.xls,.ods,.xml';

function fmtUtcDate(iso: string | null | undefined, long = false): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: long ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function fmtLocalDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function isFormRequest(row: ConsentRequest | null | undefined): boolean {
  return row?.data_route === 'customer_request' && !!(row.request_form_template ?? '').trim();
}

export function ConsentPage({ token }: ConsentPageProps) {
  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
  const [consentData, setConsentData] = useState<ConsentRequest | null>(null);
  const [card, setCard] = useState<InstructionCard | null>(null);
  const [upload, setUpload] = useState<UploadState | null>(null);
  /** Form route: the request's TrueLoad receiving address, when it has one. */
  const [receiving, setReceiving] = useState<string | null>(null);
  const [finalDecision, setFinalDecision] = useState<'consented' | 'declined' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // The upload step.
  const [readState, setReadState] = useState<'idle' | 'reading' | 'ready' | 'sending'>('idle');
  const [readFile, setReadFile] = useState<ReadFile | null>(null);
  const [readErrors, setReadErrors] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The one question after the upload.
  const [feedbackChoice, setFeedbackChoice] = useState<'matched' | 'differed' | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  // "Rather not share it?" on the download route asks once before declining.
  const [declineArmed, setDeclineArmed] = useState(false);

  // customer_request: the written request, copied for anyone whose email
  // button opens nothing.
  const [requestCopied, setRequestCopied] = useState(false);
  const [requestTextOpen, setRequestTextOpen] = useState(false);

  useEffect(() => {
    loadConsentRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function settleStatus(status: string, uploadState: UploadState | null, row: ConsentRequest | null) {
    // The form route: open until the homeowner says they submitted the
    // utility's form, declines, or the link expires. No upload.
    if (isFormRequest(row)) {
      if (status === 'declined') {
        setFinalDecision('declined');
        setPageStatus('already_responded');
      } else if (row?.customer_submitted_at) {
        setPageStatus('form_done');
      } else if (status === 'expired') {
        setPageStatus('upload_closed');
      } else {
        setPageStatus('form');
      }
      return;
    }
    // The download route: the link is open from the send until the file
    // arrives, the homeowner declines, or the window closes. An older request
    // that was authorized on the previous page lands here the same way.
    if (isUploadRoute(uploadState?.route)) {
      if (uploadState.uploadedAt) {
        setFinalDecision('consented');
        setPageStatus('uploaded');
      } else if (status === 'declined') {
        setFinalDecision('declined');
        setPageStatus('already_responded');
      } else if (uploadState.open) {
        setPageStatus('upload');
      } else {
        setPageStatus('upload_closed');
      }
      return;
    }
    if (status === 'expired') {
      setPageStatus('expired');
      return;
    }
    if (status === 'consented') {
      setFinalDecision('consented');
      setPageStatus('already_responded');
      return;
    }
    if (status === 'declined') {
      setFinalDecision('declined');
      setPageStatus('already_responded');
      return;
    }
    setPageStatus('valid');
  }

  async function lookupIp(): Promise<string> {
    try {
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipRes.json();
      return ipData.ip || '';
    } catch {
      // IP detection is non-critical
      return '';
    }
  }

  async function loadConsentRequest() {
    try {
      const response = await fetch(
        `/.netlify/functions/get-consent-request?token=${encodeURIComponent(token)}`
      );
      const result = await response.json();

      if (!response.ok) {
        setPageStatus('error');
        setErrorMessage(result.error || 'Unable to load this authorization request.');
        return;
      }

      setConsentData(result.data);
      setCard(result.card ?? null);
      setUpload(result.upload ?? null);
      setReceiving(typeof result.receiving === 'string' ? result.receiving : null);
      if (result.upload?.feedback) setFeedbackSent(true);
      settleStatus(result.status, result.upload ?? null, result.data ?? null);
    } catch {
      setPageStatus('error');
      setErrorMessage('Unable to reach the server. Please check your connection and try again.');
    }
  }

  async function handleDecision(decision: 'authorize' | 'decline') {
    setSubmitting(true);
    setErrorMessage('');

    const ip = await lookupIp();

    try {
      const response = await fetch('/.netlify/functions/record-ami-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, decision, ip }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setErrorMessage(result.error || 'Failed to record your response. Please try again.');
        setSubmitting(false);
        return;
      }

      setFinalDecision(decision === 'authorize' ? 'consented' : 'declined');
      setPageStatus('already_responded');
    } catch {
      setErrorMessage('Network error. Please try again.');
      setSubmitting(false);
    }
  }

  // The click on "Open ... request form": the one sign TrueLoad gets that the
  // homeowner went to the utility's form. Fire and forget; the link opens
  // whatever happens here.
  function recordFormOpened() {
    try {
      void fetch('/.netlify/functions/record-ami-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, decision: 'opened' }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Recording the click is a courtesy to the contractor, never a gate.
    }
  }

  async function handleSubmitted() {
    setSubmitting(true);
    setErrorMessage('');
    const ip = await lookupIp();
    try {
      const response = await fetch('/.netlify/functions/record-ami-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, decision: 'submitted', ip }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setErrorMessage(result.error || 'That did not go through. Please try again.');
        setSubmitting(false);
        return;
      }
      setConsentData(prev => (prev ? { ...prev, customer_submitted_at: result.submittedAt } : prev));
      setSubmitting(false);
      setPageStatus('form_done');
      window.scrollTo({ top: 0 });
    } catch {
      setErrorMessage('Network error. Please try again.');
      setSubmitting(false);
    }
  }

  // ── Reading the file in the browser ──────────────────────────────────────

  async function readFiles(files: File[]) {
    if (files.length === 0) return;
    setReadState('reading');
    setReadErrors([]);
    setReadFile(null);

    const datasets: Array<Array<{ timestamp: string; kw: number }>> = [];
    const errors: string[] = [];
    const qualityNotes: string[] = [];
    const flagMessages: string[] = [];
    const estimated: TimeRange[] = [];
    let generation = false;

    for (const file of files) {
      try {
        const result = await parseAMIFile(await file.arrayBuffer(), file.name);
        const picker = (result as { manualPickerRequired?: unknown }).manualPickerRequired;
        if (picker) {
          errors.push(
            `${file.name}: we could not tell which columns hold the readings. Send this file to your contractor` +
              (consentData?.contractor_email ? ` at ${consentData.contractor_email}` : '') +
              '; they can choose the columns.'
          );
          continue;
        }
        if (result.data.length === 0) {
          errors.push(`${file.name}: ${result.errors[0] ?? 'no readings could be read from this file.'}`);
          continue;
        }
        datasets.push(result.data.map(p => ({ timestamp: p.timestamp, kw: p.kw })));
        const meta = result.metadata as { dataQuality?: string[]; plausibilityFlags?: Array<{ message: string }>; generationColumnDetected?: boolean; estimatedRuns?: TimeRange[] } | undefined;
        estimated.push(...(meta?.estimatedRuns ?? []));
        for (const n of meta?.dataQuality ?? []) qualityNotes.push(files.length > 1 ? `[${file.name}] ${n}` : n);
        for (const f of meta?.plausibilityFlags ?? []) flagMessages.push(f.message);
        if (meta?.generationColumnDetected) generation = true;
      } catch (err) {
        errors.push(`${file.name}: ${err instanceof Error ? err.message : 'could not be read.'}`);
      }
    }

    if (datasets.length === 0) {
      setReadErrors(
        errors.length > 0
          ? errors
          : ['We could not read that file. Check that it is the usage or interval export from your utility, not a bill or a PDF.']
      );
      setReadState('idle');
      return;
    }

    const { data: merged, stats } = mergeAMIDatasets(datasets, 'maximum');
    if (datasets.length > 1 && shouldRefuseMerge(stats)) {
      setReadErrors(['These files overlap too much to combine. Upload one file that covers the whole period, or send them to your contractor.']);
      setReadState('idle');
      return;
    }
    const summary = summarizeAmiSeries(merged);
    if ((summary.peakKw ?? 0) > PEAK_CEILING_KW) {
      setReadErrors([
        'The readings in this file are far higher than a home uses. It may be the wrong export, or a whole-building meter. Send the file to your contractor rather than uploading it here.',
      ]);
      setReadState('idle');
      return;
    }

    // The same regrouping the contractor's upload does (AMIDataInput.finishUpload).
    const estimatedRuns = regroupEstimatedRuns(merged, estimated, summary.intervalMinutes);

    setReadFile({
      points: merged,
      qualityNotes,
      flagMessages,
      estimatedRuns,
      generation,
      fileName: files.map(f => f.name).join(', '),
      count: summary.count,
      first: summary.firstTimestamp,
      last: summary.lastTimestamp,
      intervalMinutes: summary.intervalMinutes,
      measurementPeriod: summary.measurementPeriod,
    });
    setReadErrors(errors);
    setReadState('ready');
  }

  async function sendUpload() {
    if (!readFile) return;
    setReadState('sending');
    setErrorMessage('');
    // Sending the file is the authorization, so it is recorded the way
    // I Authorize is on the other route: with the homeowner's IP.
    const ip = await lookupIp();
    try {
      const response = await fetch('/.netlify/functions/homeowner-upload-intervals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ip,
          fileName: readFile.fileName,
          rows: readFile.points.map(p => ({ ts: p.timestamp, kw: p.kw })),
          summary: {
            intervalMinutes: readFile.intervalMinutes,
            measurementPeriod: readFile.measurementPeriod,
            qualityNotes: readFile.qualityNotes.length > 0 ? 'PARSING NOTES:\n- ' + readFile.qualityNotes.join('\n- ') : '',
            generationColumnDetected: readFile.generation,
            flagMessages: readFile.flagMessages,
            estimatedRuns: readFile.estimatedRuns,
          },
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setErrorMessage(result.error || 'The upload could not be saved. Please try again.');
        setReadState('ready');
        return;
      }
      setUpload(u => ({
        route: u?.route ?? 'self_download',
        open: false,
        expiresAt: u?.expiresAt ?? null,
        uploadedAt: new Date().toISOString(),
        readingCount: result.inserted ?? readFile.count,
        firstReading: result.firstReading ?? readFile.first,
        lastReading: result.lastReading ?? readFile.last,
        feedback: null,
      }));
      setPageStatus('uploaded');
    } catch {
      setErrorMessage('Network error. Please try again.');
      setReadState('ready');
    }
  }

  async function sendFeedback() {
    if (!feedbackChoice) return;
    setFeedbackBusy(true);
    try {
      await fetch('/.netlify/functions/homeowner-instructions-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, matched: feedbackChoice === 'matched', note: feedbackNote }),
      });
    } catch {
      // The answer is a courtesy; a failed send is not the homeowner's problem.
    } finally {
      setFeedbackBusy(false);
      setFeedbackSent(true);
    }
  }

  const expiresAt = consentData?.token_expires_at ? new Date(consentData.token_expires_at) : null;
  const utilityName = card?.name || consentData?.utility_name || 'your electric utility';
  const contractorLabel = consentData
    ? [consentData.contractor_name, consentData.company_name].filter(Boolean).join(', ')
    : 'your contractor';
  const { steps, generic } = stepsFor(card);
  const portal = safePortalUrl(card?.portal_url);
  const history = describeHistory(card?.history_months);

  const customerRequest = consentData?.data_route === 'customer_request';
  const requestTo = (consentData?.utility_email ?? '').trim();
  const requestEmail = consentData
    ? buildCustomerRequestEmail({
        utilityName: utilityName,
        customerName: consentData.customer_name,
        serviceAddress: consentData.customer_address,
        accountNumber: consentData.account_number,
        contractorName: consentData.contractor_name,
        companyName: consentData.company_name,
      })
    : null;

  // The form route: the utility's form, filled with what the request holds.
  // Dates are worked out today, so a link opened late still asks for the
  // latest 13 months. The utility sends the file to the request's TrueLoad
  // receiving address when it has one (it loads and is forwarded to the
  // contractor), else to the contractor's own email.
  const formRequest = isFormRequest(consentData);
  const formUrl =
    consentData && formRequest && isUsableTemplate(consentData.request_form_template)
      ? fillRequestForm(
          consentData.request_form_template,
          requestFormValues(
            {
              utilityName,
              customerName: consentData.customer_name,
              customerEmail: consentData.customer_email,
              customerPhone: consentData.customer_phone,
              serviceAddress: consentData.customer_address,
              accountNumber: consentData.account_number,
              contractorName: consentData.contractor_name,
              companyName: consentData.company_name,
              recipientEmail: receiving ?? consentData.contractor_email,
            },
            localDate(new Date()),
          ),
        )
      : null;
  const contractorShort = consentData?.company_name || consentData?.contractor_name || 'your contractor';

  async function copyRequest() {
    if (!requestEmail) return;
    const text = [requestTo ? `To: ${requestTo}` : '', `Subject: ${requestEmail.subject}`, '', requestEmail.body]
      .filter((line, i) => i > 0 || line)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setRequestCopied(true);
      setTimeout(() => setRequestCopied(false), 2500);
    } catch {
      // No clipboard: the text is on screen to select by hand.
      setRequestTextOpen(true);
    }
  }

  const requestCard = requestEmail && (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="homeowner-request">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Step 1: Ask {utilityName} for your data</h3>
      <p className="text-sm text-gray-600 mb-4">
        {utilityName} asks that requests for usage data come from the account holder, so this one goes from your
        own email. We have written it for you: the button opens it in your email app, addressed and ready. Read it
        over and press send.
      </p>
      <a
        href={mailtoHref(requestTo, requestEmail)}
        data-testid="homeowner-request-open-email"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
      >
        <Mail size={16} /> Open the email to {utilityName}
      </a>
      <div className="mt-4 text-sm">
        <p className="text-gray-600">
          Button did nothing?{' '}
          <button
            type="button"
            onClick={() => void copyRequest()}
            data-testid="homeowner-request-copy"
            className="inline-flex items-center gap-1 font-medium text-blue-700 underline hover:text-blue-900"
          >
            {requestCopied ? <Check size={14} /> : <Copy size={14} />}
            {requestCopied ? 'Copied' : 'Copy the request'}
          </button>{' '}
          and paste it into a new email{requestTo ? <> to <strong className="break-words">{requestTo}</strong></> : null}.{' '}
          <button
            type="button"
            onClick={() => setRequestTextOpen(o => !o)}
            data-testid="homeowner-request-show"
            className="text-blue-700 underline hover:text-blue-900"
          >
            {requestTextOpen ? 'Hide it' : 'Show it'}
          </button>
        </p>
        {requestTextOpen && (
          <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-gray-800" data-testid="homeowner-request-text">
            {requestTo && <p className="text-xs text-gray-500 mb-1">To: {requestTo}</p>}
            <p className="text-xs text-gray-500 mb-2">Subject: {requestEmail.subject}</p>
            <p className="whitespace-pre-wrap select-all">{requestEmail.body}</p>
          </div>
        )}
      </div>
      <p className="mt-4 text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-md p-3">
        {utilityName} replies to you with the file, usually by email. When it arrives, come back to this page (the
        link in the email from TrueLoad) and do step 2.
      </p>
    </div>
  );

  const instructionCard = (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="homeowner-instructions">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Step 1: Download your usage data from {utilityName}</h3>
      <p className="text-sm text-gray-600 mb-4">
        {generic
          ? 'Most utility websites export interval data the same way. If yours looks different, follow the closest option.'
          : `These steps were written for ${utilityName}.`}
        {card?.export_format ? ` The export is ${card.export_format}.` : ''}
        {history ? ` The portal offers ${history}.` : ''}
      </p>
      {portal && (
        <a
          href={portal}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mb-4 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Open {utilityName}'s website <ExternalLink size={14} />
        </a>
      )}
      <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-800" data-testid="homeowner-steps">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      {card?.rollout_note && (
        <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">{card.rollout_note}</p>
      )}
      {!generic && !card?.instructions_verified && (
        <p className="mt-4 text-xs text-gray-500">
          These steps were written from {utilityName}'s help pages and have not yet been confirmed by a customer. If they do not match what you see, we ask at the end.
        </p>
      )}
    </div>
  );

  const uploadBox = (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="homeowner-upload">
      <h3 className="text-base font-semibold text-gray-900 mb-1">
        {customerRequest ? `Step 2: When ${utilityName} sends the file, upload it here` : 'Step 2: Upload the file here'}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Green Button XML, CSV or Excel. The file is read on your device; only the readings go to your contractor's project.
      </p>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          void readFiles(Array.from(e.dataTransfer.files));
        }}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
      >
        <Upload className="mx-auto text-gray-400 mb-2" size={28} />
        <p className="text-sm text-gray-700 mb-3">Drop the file here, or</p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={readState === 'reading' || readState === 'sending'}
          data-testid="homeowner-choose-file"
          className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Choose a file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={e => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void readFiles(files);
          }}
        />
      </div>

      {readState === 'reading' && <p className="mt-3 text-sm text-gray-600">Reading the file...</p>}

      {readErrors.length > 0 && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-1" data-testid="homeowner-read-errors">
          {readErrors.map((e, i) => (
            <p key={i}>{e}</p>
          ))}
        </div>
      )}

      {readFile && (readState === 'ready' || readState === 'sending') && (
        <div className="mt-4 rounded-md bg-green-50 border border-green-200 p-4" data-testid="homeowner-read-summary">
          <p className="text-sm text-green-900">
            We read <strong>{readFile.count.toLocaleString()}</strong> readings
            {readFile.first && readFile.last ? <> from <strong>{fmtUtcDate(readFile.first)}</strong> to <strong>{fmtUtcDate(readFile.last)}</strong></> : null}
            {readFile.intervalMinutes ? <>, every {readFile.intervalMinutes} minutes</> : null}.
          </p>
          {readFile.first && readFile.last && (new Date(readFile.last).getTime() - new Date(readFile.first).getTime()) < 300 * 24 * 3600 * 1000 && (
            <p className="mt-2 text-xs text-green-900">
              That is less than a year. The evaluation needs 12 months where possible;{' '}
              {customerRequest
                ? `if ${utilityName} has more, you can ask them for the longer range before sending.`
                : 'if the portal offered a longer range, you can go back and export it before sending.'}
            </p>
          )}
          {errorMessage && (
            <p className="mt-2 text-sm text-red-700">{errorMessage}</p>
          )}
          <button
            type="button"
            onClick={sendUpload}
            disabled={readState === 'sending'}
            data-testid="homeowner-send-upload"
            className="mt-3 w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:bg-green-400 font-semibold text-base touch-manipulation"
          >
            {readState === 'sending' ? 'Sending...' : 'Send to my contractor'}
          </button>
          <p className="mt-3 text-xs text-gray-600 leading-relaxed" data-testid="homeowner-consent-line">
            By sending, you confirm this is the usage data for your electric account and you authorize{' '}
            {contractorLabel} to use it for the electrical service evaluation at{' '}
            {consentData?.customer_address || 'your address'} under the National Electrical Code&rsquo;s
            measured-demand method. It is used for nothing else. Your IP address is recorded with the
            authorization.
          </p>
        </div>
      )}
    </div>
  );

  const declineBlock = (
    <div className="text-center text-sm text-gray-500 pb-2" data-testid="homeowner-decline">
      {!declineArmed ? (
        <p>
          Rather not share it?{' '}
          <button
            type="button"
            onClick={() => setDeclineArmed(true)}
            data-testid="homeowner-decline-open"
            className="underline hover:text-gray-700"
          >
            Tell your contractor no
          </button>
        </p>
      ) : (
        <div className="inline-flex flex-col sm:flex-row items-center gap-2">
          <span>This closes the link and tells {consentData?.contractor_name || 'your contractor'} you are not sharing your data.</span>
          <button
            type="button"
            onClick={() => handleDecision('decline')}
            disabled={submitting}
            data-testid="homeowner-decline-confirm"
            className="px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Sending...' : 'Yes, decline'}
          </button>
          <button
            type="button"
            onClick={() => setDeclineArmed(false)}
            disabled={submitting}
            className="underline hover:text-gray-700"
          >
            Go back
          </button>
        </div>
      )}
      {errorMessage && readState !== 'ready' && readState !== 'sending' && (
        <p className="mt-2 text-red-700">{errorMessage}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-4 py-4 shadow-sm">
        <div className="max-w-xl mx-auto">
          <img src="/trueload_logo.svg" alt="TrueLoad" className="h-12 w-auto" />
        </div>
      </header>

      <main className="flex-1 max-w-xl mx-auto w-full px-4 py-8">
        {pageStatus === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 text-sm">Loading authorization request...</p>
          </div>
        )}

        {pageStatus === 'error' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Not Found</h2>
            <p className="text-gray-600 text-sm">
              {errorMessage || 'This authorization link is invalid or has expired.'}
            </p>
          </div>
        )}

        {pageStatus === 'expired' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Clock className="mx-auto text-orange-500 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authorization Link Expired
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              This authorization link has expired. Please contact your contractor to request a new one.
            </p>
            {consentData && (
              <p className="text-sm text-gray-500">
                Requested by: {consentData.contractor_name}, {consentData.company_name}
              </p>
            )}
          </div>
        )}

        {pageStatus === 'upload_closed' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-testid="homeowner-upload-closed">
            <Clock className="mx-auto text-orange-500 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">This Link Has Closed</h2>
            <p className="text-gray-600 text-sm mb-4">
              {formRequest
                ? 'The time for requesting your usage data has passed. Ask your contractor for a new link.'
                : consentData?.data_route === 'customer_request'
                ? 'The time for sending your usage data has passed. Ask your contractor for a new link; if the utility has already sent you the file, you can upload it there.'
                : 'The two weeks for sending your usage data have passed. Ask your contractor for a new link; the download steps will be waiting on it.'}
            </p>
            {consentData && (
              <p className="text-sm text-gray-500">
                Contractor: {consentData.contractor_name}, {consentData.company_name}
                {consentData.contractor_email ? ` (${consentData.contractor_email})` : ''}
              </p>
            )}
          </div>
        )}

        {pageStatus === 'already_responded' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            {finalDecision === 'consented' ? (
              <>
                <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Authorization Confirmed
                </h2>
                <p className="text-gray-600 text-sm">
                  You have authorized the release of your utility data. Your contractor has been notified and a data request has been sent to your utility provider.
                </p>
              </>
            ) : (
              <>
                <XCircle className="mx-auto text-red-500 mb-4" size={48} />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {isUploadRoute(upload?.route) ? 'No Data Shared' : 'Authorization Declined'}
                </h2>
                <p className="text-gray-600 text-sm">
                  {isUploadRoute(upload?.route)
                    ? 'You chose not to share your usage data. Your contractor has been notified.'
                    : 'You have declined this authorization request. Your contractor has been notified.'}
                </p>
              </>
            )}
          </div>
        )}

        {pageStatus === 'upload' && consentData && (
          <div className="space-y-5" data-testid="homeowner-upload-step">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Share your electricity usage data with {consentData.contractor_name || 'your contractor'}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {contractorLabel} is evaluating the electrical service at{' '}
                <strong>{consentData.customer_address}</strong> and needs 12 months of usage readings from{' '}
                {utilityName}.{' '}
                {customerRequest
                  ? `Two steps: send ${utilityName} a request from your own email, then upload the file they send you. Keep the email with this link; you come back here to upload.`
                  : 'Two steps, about five minutes. Your contractor never sees your utility login.'}
              </p>
              {upload?.expiresAt && (
                <p className="text-sm text-orange-600 mt-3 font-medium">
                  This link is open until {fmtLocalDate(upload.expiresAt)}.
                </p>
              )}
            </div>
            {customerRequest ? requestCard : instructionCard}
            {uploadBox}
            {declineBlock}
          </div>
        )}

        {pageStatus === 'form' && consentData && (
          <div className="space-y-5" data-testid="homeowner-form-step">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Request your usage data from {utilityName}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {contractorLabel} is evaluating the electrical service at{' '}
                <strong>{consentData.customer_address}</strong> and needs your electricity usage records.{' '}
                {utilityName} asks that the request come from you, through its website. It takes a few minutes,
                and we have filled in what we know.
              </p>
              {expiresAt && (
                <p className="text-sm text-orange-600 mt-3 font-medium">
                  This link is open until {fmtLocalDate(consentData.token_expires_at)}.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="homeowner-form">
              <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-800 mb-5" data-testid="homeowner-form-steps">
                <li>Press the button below. {utilityName}&rsquo;s request form opens in a new tab, already filled in.</li>
                <li>Check the details and fill in anything left blank. Your account number is printed on your bill.</li>
                <li>Sign the waiver on the form, then submit it.</li>
                <li>Come back to this tab and press <strong>I submitted the form</strong>.</li>
              </ol>
              {formUrl ? (
                <a
                  href={formUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={recordFormOpened}
                  data-testid="homeowner-form-open"
                  className="flex w-full items-center justify-center gap-2 py-4 px-4 rounded-xl bg-blue-600 text-white text-lg font-semibold text-center hover:bg-blue-700 shadow-sm touch-manipulation"
                >
                  Open {utilityName}&rsquo;s request form <ExternalLink size={18} />
                </a>
              ) : (
                <p className="text-sm text-red-700">
                  The link to {utilityName}&rsquo;s form is missing. Please contact {contractorShort}.
                </p>
              )}
              <p
                className="mt-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3"
                data-testid="homeowner-form-waiver"
              >
                <strong>You sign the waiver yourself.</strong> {utilityName}&rsquo;s form includes a waiver that only the
                account holder can sign. TrueLoad never fills it in or signs it for you.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Submitted it?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Once you have submitted the form on {utilityName}&rsquo;s website, press this so {contractorShort} knows
                the request is on its way.
              </p>
              {errorMessage && <p className="mb-3 text-sm text-red-700">{errorMessage}</p>}
              <button
                type="button"
                onClick={() => void handleSubmitted()}
                disabled={submitting}
                data-testid="homeowner-form-submitted"
                className="w-full py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 disabled:bg-green-400 font-semibold text-lg shadow-sm touch-manipulation"
              >
                {submitting ? 'Saving...' : 'I submitted the form'}
              </button>
            </div>
            {declineBlock}
          </div>
        )}

        {pageStatus === 'form_done' && consentData && (
          <div className="space-y-5" data-testid="homeowner-form-done">
            <div className="bg-white rounded-lg shadow-sm border border-green-200 p-8 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="text-green-600" size={52} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">You&rsquo;re all done. Thank you!</h2>
              <p className="text-base text-gray-700">
                You did everything that was asked of you. Your request is with {utilityName}.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">What happens next</h3>
              <ul className="space-y-3 text-sm text-gray-800">
                <li className="flex gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                  <span>{utilityName} gathers your usage data, usually within {REQUEST_FORM_BUSINESS_DAYS} business days.</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                  <span>They send it straight to {contractorShort}.</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                  <span>{contractorShort} uses it to evaluate your electrical service.</span>
                </li>
              </ul>
              <p className="mt-5 rounded-md bg-green-50 border border-green-200 p-4 text-base font-medium text-green-900" data-testid="homeowner-form-nothing-left">
                There is nothing else for you to do. You don&rsquo;t need to upload anything, reply to anyone, or come
                back to this page.
              </p>
              {consentData.customer_submitted_at && (
                <p className="mt-3 text-xs text-gray-500">
                  Recorded {new Date(consentData.customer_submitted_at).toLocaleString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}.
                </p>
              )}
            </div>
            <p className="text-center text-sm text-gray-500">You can close this page.</p>
          </div>
        )}

        {pageStatus === 'uploaded' && (
          <div className="space-y-5" data-testid="homeowner-uploaded">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">All done. Thank you.</h2>
              <p className="text-gray-600 text-sm">
                {upload?.readingCount ? <><strong>{upload.readingCount.toLocaleString()}</strong> readings</> : 'Your readings'}
                {upload?.firstReading && upload?.lastReading ? <> from {fmtUtcDate(upload.firstReading)} to {fmtUtcDate(upload.lastReading)}</> : null}
                {' '}are on your contractor's project, and they have been notified. Sending the file recorded your
                authorization for this evaluation.
              </p>
            </div>

            {!feedbackSent && upload?.route === 'self_download' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="homeowner-feedback">
                <h3 className="text-base font-semibold text-gray-900 mb-1">One last thing</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Did the download steps match what you saw on {utilityName}'s website? Your answer fixes them for the next person.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setFeedbackChoice('matched')}
                    data-testid="homeowner-feedback-matched"
                    className={`flex-1 py-2 rounded-md border text-sm font-medium ${feedbackChoice === 'matched' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                  >
                    Yes, they matched
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeedbackChoice('differed')}
                    data-testid="homeowner-feedback-differed"
                    className={`flex-1 py-2 rounded-md border text-sm font-medium ${feedbackChoice === 'differed' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                  >
                    No, something was different
                  </button>
                </div>
                {feedbackChoice === 'differed' && (
                  <textarea
                    value={feedbackNote}
                    onChange={e => setFeedbackNote(e.target.value)}
                    placeholder="What was different? Which menu, which button, what the file was called..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-3 min-h-[88px]"
                    maxLength={1000}
                  />
                )}
                <button
                  type="button"
                  onClick={sendFeedback}
                  disabled={!feedbackChoice || feedbackBusy}
                  data-testid="homeowner-feedback-send"
                  className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {feedbackBusy ? 'Sending...' : 'Send'}
                </button>
              </div>
            )}
            {(feedbackSent || upload?.route !== 'self_download') && (
              <p className="text-center text-sm text-gray-500">Thanks. You can close this page.</p>
            )}
          </div>
        )}

        {pageStatus === 'valid' && consentData && (
          <div className="space-y-5">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Utility Data Authorization Request
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Your contractor is requesting authorization to obtain your utility AMI (Advanced Metering Infrastructure) interval data for a service load evaluation under the National Electrical Code’s measured-demand method (Section 220.87; Section 120.87 in the 2026 edition).
              </p>
              {expiresAt && (
                <p className="text-sm text-orange-600 mt-3 font-medium">
                  This request expires{' '}
                  {expiresAt.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}{' '}
                  at{' '}
                  {expiresAt.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  .
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Request Details</h3>
              <dl className="space-y-3">
                <div className="flex justify-between text-sm gap-4">
                  <dt className="text-gray-600 font-medium shrink-0">Your Name</dt>
                  <dd className="text-gray-900 text-right">{consentData.customer_name}</dd>
                </div>
                <div className="flex justify-between text-sm gap-4">
                  <dt className="text-gray-600 font-medium shrink-0">Service Address</dt>
                  <dd className="text-gray-900 text-right">{consentData.customer_address}</dd>
                </div>
                {consentData.account_number && (
                  <div className="flex justify-between text-sm gap-4">
                    <dt className="text-gray-600 font-medium shrink-0">Account Number</dt>
                    <dd className="text-gray-900 text-right">{consentData.account_number}</dd>
                  </div>
                )}
                <div className="border-t border-gray-100 my-1" />
                <div className="flex justify-between text-sm gap-4">
                  <dt className="text-gray-600 font-medium shrink-0">Contractor</dt>
                  <dd className="text-gray-900 text-right">{consentData.contractor_name}</dd>
                </div>
                <div className="flex justify-between text-sm gap-4">
                  <dt className="text-gray-600 font-medium shrink-0">Company</dt>
                  <dd className="text-gray-900 text-right">{consentData.company_name}</dd>
                </div>
                {consentData.utility_name && (
                  <div className="flex justify-between text-sm gap-4">
                    <dt className="text-gray-600 font-medium shrink-0">Utility</dt>
                    <dd className="text-gray-900 text-right">{consentData.utility_name}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 leading-relaxed">
                <strong>What you're authorizing:</strong>{' '}
                By clicking "I Authorize", you are consenting to release your 12-month AMI interval data (kWh usage readings) to your contractor for the purpose of calculating your existing electrical service capacity under the National Electrical Code’s measured-demand method. Your data will only be used for this evaluation.
              </p>
            </div>

            {errorMessage && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div className="space-y-3 pb-4">
              <button
                onClick={() => handleDecision('authorize')}
                disabled={submitting}
                className="w-full py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 disabled:bg-green-400 transition-colors font-semibold text-lg shadow-sm touch-manipulation"
              >
                {submitting ? 'Processing...' : 'I Authorize'}
              </button>
              <button
                onClick={() => handleDecision('decline')}
                disabled={submitting}
                className="w-full py-3 bg-white border-2 border-red-400 text-red-600 rounded-xl hover:bg-red-50 active:bg-red-100 disabled:opacity-50 transition-colors font-medium touch-manipulation"
              >
                I Do Not Authorize
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center pb-2">
              Your IP address will be recorded for authorization verification purposes.
            </p>
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-gray-200 px-4 py-4 text-center">
        <p className="text-xs text-gray-400">
          Powered by TrueLoad &middot; NEC Service Load Evaluation
        </p>
      </footer>
    </div>
  );
}
